import type { CSSProperties, FC, ReactNode } from 'react'

import { useId } from 'react'
import {
    box,
    cross,
    cylinder,
    project,
    round
} from '@/lib/iso'

interface SwitchIllustrationProps {
    className?: string
}

const PINS = [box(-14, 6, -62, 1.5, 1.5, 26), box(12, -12, -62, 1.5, 1.5, 26)]
const CONTACT = box(-4, 16, -50, 8, 1, 16)
const BOTTOM_HOUSING = box(0, 0, 0, 30, 30, 16)
const LED_SLOT = box(0, -18, 16, 6, 3, 0.5)
const POST = cylinder(0, 0, 7, 16, 26)
const SPRING_RADIUS = cylinder(0, 0, 8, 0, 0)
const SPRING_COILS = Array.from({ length: 10 }, (_, index) => project([0, 0, 36 + index * 5.6])[1])
const TOP_HOUSING = box(0, 0, 104, 31, 31, 14)
const HOLE = cylinder(0, 0, 12, 118, 118)
const CLIP_FRONT = box(0, 32.5, 100, 7, 1.5, 20)
const CLIP_RIGHT = box(32.5, 0, 100, 1.5, 7, 20)
const STEM = cylinder(0, 0, 12, 138, 152)
const STEM_CROSS = cross(0, 0, 152, 9, 2.4, 7)

const part = (delay: number, distance: number): CSSProperties => ({
    '--delay': `${delay}s`,
    '--float': `${distance}px`
} as CSSProperties)

const SwitchIllustration: FC<SwitchIllustrationProps> = ({ className }): ReactNode => {
    const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
    const ref = (name: string) => `${uid}-${name}`
    const url = (name: string) => `url(#${ref(name)})`
    const springLeft = round(-SPRING_RADIUS.rx)
    const springRight = round(SPRING_RADIUS.rx)

    return (
        <svg
            viewBox='-78 -188 156 270'
            role='img'
            aria-label='Exploded view of a Nano Blush linear switch: stem, translucent top housing, spring, bottom housing and contacts'
            className={className}
        >
            <defs>
                <radialGradient id={ref('glow')} cx='0' cy='-60' r='120' gradientUnits='userSpaceOnUse'>
                    <stop offset='0' stopColor='#ff6a4d' stopOpacity='0.16' />
                    <stop offset='0.5' stopColor='#ff6a4d' stopOpacity='0.04' />
                    <stop offset='1' stopColor='#ff6a4d' stopOpacity='0' />
                </radialGradient>

                <linearGradient id={ref('copper')} x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='0' stopColor='#e8a27a' />
                    <stop offset='1' stopColor='#8a4c2c' />
                </linearGradient>

                <linearGradient id={ref('stem')} x1='0' y1='0' x2='1' y2='0'>
                    <stop offset='0' stopColor='#8f3520' />
                    <stop offset='0.45' stopColor='#c4553a' />
                    <stop offset='1' stopColor='#6e2716' />
                </linearGradient>

                <linearGradient id={ref('post')} x1='0' y1='0' x2='1' y2='0'>
                    <stop offset='0' stopColor='#4c4c53' />
                    <stop offset='0.5' stopColor='#7a7a82' />
                    <stop offset='1' stopColor='#414147' />
                </linearGradient>
            </defs>

            <ellipse cx='0' cy='-60' rx='120' ry='150' fill={url('glow')} />

            <g className='float-y' style={part(0.6, -5)}>
                <polygon points={CONTACT.left} fill='#9b5a37' />
                <polygon points={CONTACT.right} fill='#7a4428' />
                <polygon points={CONTACT.top} fill='#e3a27c' />

                {PINS.map((pin, index) => (
                    <g key={index}>
                        <polygon points={pin.left} fill={url('copper')} />
                        <polygon points={pin.right} fill='#80492b' />
                        <polygon points={pin.top} fill='#f0b892' />
                    </g>
                ))}
            </g>

            <g className='float-y' style={part(0.3, -4)}>
                <polygon points={BOTTOM_HOUSING.left} fill='#4a4a50' />
                <polygon points={BOTTOM_HOUSING.right} fill='#36363b' />
                <polygon points={BOTTOM_HOUSING.top} fill='#6e6e75' stroke='rgb(255 255 255 / 0.14)' strokeWidth='0.6' />
                <polygon points={LED_SLOT.top} fill='#1d1d21' />
                <path d={POST.side} fill={url('post')} />
                <ellipse cx={POST.cx} cy={POST.topY} rx={POST.rx} ry={POST.ry} fill='#8d8d95' />
            </g>

            <g className='float-y' style={part(0.9, -6)}>
                {SPRING_COILS.map(y => (
                    <path
                        key={`back-${y}`}
                        d={`M ${springLeft} ${y} A ${SPRING_RADIUS.rx} ${SPRING_RADIUS.ry} 0 0 1 ${springRight} ${y}`}
                        fill='none'
                        stroke='#6c6c73'
                        strokeWidth='1.5'
                    />
                ))}

                {SPRING_COILS.map(y => (
                    <path
                        key={`front-${y}`}
                        d={`M ${springLeft} ${y} A ${SPRING_RADIUS.rx} ${SPRING_RADIUS.ry} 0 0 0 ${springRight} ${y}`}
                        fill='none'
                        stroke='#dadae0'
                        strokeWidth='1.6'
                        strokeLinecap='round'
                    />
                ))}
            </g>

            <g className='float-y' style={part(0.15, -7)}>
                <polygon points={TOP_HOUSING.left} fill='rgb(255 214 206 / 0.12)' stroke='rgb(255 255 255 / 0.4)' strokeWidth='0.6' />
                <polygon points={TOP_HOUSING.right} fill='rgb(255 214 206 / 0.07)' stroke='rgb(255 255 255 / 0.3)' strokeWidth='0.6' />
                <polygon points={TOP_HOUSING.top} fill='rgb(255 226 220 / 0.18)' stroke='rgb(255 255 255 / 0.55)' strokeWidth='0.7' />
                <ellipse cx={HOLE.cx} cy={HOLE.topY} rx={HOLE.rx} ry={HOLE.ry} fill='rgb(12 12 14 / 0.6)' stroke='rgb(255 255 255 / 0.35)' strokeWidth='0.6' />

                <polygon points={CLIP_RIGHT.left} fill='#b4462d' />
                <polygon points={CLIP_RIGHT.right} fill='#8f3420' />
                <polygon points={CLIP_RIGHT.top} fill='#e46a4b' />
                <polygon points={CLIP_FRONT.left} fill='#c94f33' />
                <polygon points={CLIP_FRONT.right} fill='#93371f' />
                <polygon points={CLIP_FRONT.top} fill='#ec7453' />
            </g>

            <g className='float-y' style={part(0, -9)}>
                <path d={STEM.side} fill={url('stem')} />
                <ellipse cx={STEM.cx} cy={STEM.topY} rx={STEM.rx} ry={STEM.ry} fill='#d4613f' />

                {STEM_CROSS.sides.map((side, index) => (
                    <polygon
                        key={index}
                        points={side}
                        fill={index % 2 === 0 ? '#d7a798' : '#bf8d7e'}
                    />
                ))}

                <polygon points={STEM_CROSS.top} fill='#f7ddd2' />
            </g>
        </svg>
    )
}

export default SwitchIllustration
